import { Button, Grow, Stack, TextField, Typography } from "@mui/material";
import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTokenMaker } from "../../hooks";
const logo = "/airplx-logo-transparent-square-no-text.png";

export const ForgotPassword: React.FC<{}> = () => {
  const { url: tokenMakerURL } = useTokenMaker();
  const location = useLocation();
  const navigate = useNavigate();

  const [email, setEmail] = React.useState<string>(location.state?.email || "");
  const [sent, setSent] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>("");

  const handleSendReset = async () => {
    if (!email) {
      setError("Please enter your email address");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${tokenMakerURL}/send-password-reset`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setSent(true);
    } catch (err) {
      setError("Failed to send reset email. Please try again.");
      console.error("Error sending password reset:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Stack
        direction="column"
        justifyContent="center"
        alignItems="center"
        height="60vh"
        width="100vw"
      >
        <Stack direction="column" width={450} spacing={3}>
          <Grow in={!sent}>
            <Stack direction="column" spacing={3} alignItems="center">
              <Stack spacing={1} alignItems="center">
                <Typography variant="h5">Reset Your Password</Typography>
                <Typography variant="body1" color="text.secondary" align="center">
                  Enter your email to receive a reset link
                </Typography>
              </Stack>

              <TextField
                fullWidth
                label="Email"
                variant="outlined"
                type="email"
                onChange={(e) => setEmail(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSendReset()}
                value={email}
                error={Boolean(error)}
                helperText={error}
                disabled={loading}
                autoFocus
              />

              <Button
                disabled={!email || loading}
                variant="contained"
                color="primary"
                onClick={handleSendReset}
                fullWidth
                sx={{ borderRadius: 1 }}
              >
                {loading ? "Sending..." : "Send Reset Link"}
              </Button>

              <Button
                size="small"
                onClick={() => navigate("/login")}
              >
                Back to login
              </Button>
            </Stack>
          </Grow>
          <Grow in={sent}>
            <Stack direction="column" spacing={2} alignItems="center">
              <Typography variant="h5">Check Your Email</Typography>
              <Typography variant="body1" color="text.secondary" align="center">
                We sent a password reset link to<br />
                <strong>{email}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center">
                The link will expire in 1 hour
              </Typography>
              <Button
                variant="outlined"
                onClick={() => navigate("/login")}
                sx={{ mt: 2 }}
              >
                Back to login
              </Button>
            </Stack>
          </Grow>
        </Stack>
      </Stack>
      <img
        src={logo}
        style={{ position: "fixed", bottom: 15, right: 15, width: 50 }}
      />
    </>
  );
};
